import styled from 'styled-components';

import { Label } from '@common/Label';
import { getCallCredentials, VoiceCallCredential } from '@requests/telnyx';
import { useQuery } from '@tanstack/react-query';
import { CallCredentials } from './CallCredentials';
import { useDispatch, useSelector } from 'react-redux';
import { CallCredential, flowActions, State } from '@store/index';
import { Alert, Stack } from '@mui/material';
import { Button } from '@common/Button';
import { DialerSipOutlined } from '@mui/icons-material';
import { useTheme } from '../../../styles';
import { RequestError } from '@requests/common';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  grid-gap: 8px;
`;

const Credential = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  grid-gap: 8px;
  border: 1px solid ${({ theme }) => theme.line.light};
  border-radius: 8px;
  padding: 6px 8px;
  padding-left: 10px;

  p {
    font-size: 14px;
    padding-left: 4px;
    color: ${({ theme }) => theme.text.standard};
  }
`;

export const CallCredentialAssign: React.FC = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const callCredential = useSelector((state: State) => state.flow.callCredential);

  const {
    data: credentials = [],
    isLoading: credentialsLoading,
    error,
  } = useQuery<VoiceCallCredential[], RequestError>({
    queryKey: ['call_credentials'],
    queryFn: getCallCredentials,
    retry: false,
  });

  const errorMessage = error?.response?.data?.error || error?.message;
  const connectionId = callCredential?.id;

  const changeCallCredential = (credentialId: string) => {
    const credential = credentials.find((credential) => credential.id === credentialId);
    if (!credential) return;

    const newCallCredential: CallCredential = {
      id: credential.id,
      username: credential.user_name,
      password: credential.password,
    };

    localStorage.setItem('callCredential', JSON.stringify(newCallCredential));
    dispatch(flowActions.setCallCredential(newCallCredential));
  };

  const description =
    'To test this workflow inside Flow, you need to make an inbound call. By assigning a call credential from your Telnyx portal account, you can execute this workflow from the user interface and see the nodes execute live.';

  if (credentials.length === 0 && !credentialsLoading) {
    return (
      <Container>
        <Alert severity='warning' sx={{ mb: 1 }}>
          There were no call credentials found in your account. You can create one{' '}
          <a
            style={{ color: theme.primary }}
            href={
              window.location.origin.includes('dev')
                ? 'https://portaldev.telnyx.com/#/voice/connections'
                : 'https://portal.telnyx.com/#/voice/connections'
            }
            target='_blank'
            rel='noreferrer'
          >
            here
          </a>
          . This is required to run this call workflow within the Flow user interface.
        </Alert>
      </Container>
    );
  }

  return (
    <Container>
      <Alert severity='info' sx={{ mb: 1 }}>
        To run this call workflow within the user interface, a credential connection from your Telnyx portal account
        needs to be selected from the dropdown below. This credential will be used to dial your voice application and
        start the workflow. If you do not have any credentials in your account, you can create one{' '}
        <a
          style={{ color: theme.primary }}
          href={
            window.location.origin.includes('dev')
              ? 'https://portaldev.telnyx.com/#/voice/connections'
              : 'https://portal.telnyx.com/#/voice/connections'
          }
          target='_blank'
          rel='noreferrer'
        >
          here
        </a>
        .
      </Alert>
      <Alert severity='warning' sx={{ mb: 1 }}>
        {`Only credential connections with an outbound voice profile and "Park Outbound Calls" disabled will show in the dropdown below. Once this is setup, this credential will be used for all your inbound testing calls across the application.`}
      </Alert>
      <Label title='Call Credential' tooltip={description} />
      {errorMessage ? (
        <Alert severity='error'>{errorMessage}</Alert>
      ) : (
        <CallCredentials
          value={connectionId || ''}
          loading={credentialsLoading}
          labels={credentials.map((credential, index) => ({
            id: index,
            label: credential.user_name,
            value: credential.id,
            metadata: credential,
          }))}
          onChange={(e, newValue) => changeCallCredential(newValue.value)}
          placeholder='Call Credential'
        />
      )}
    </Container>
  );
};

export const CallCredentialView = () => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const callCredential = useSelector((state: State) => state.flow.callCredential);
  const username = callCredential?.username;

  const changeCredential = () => {
    localStorage.removeItem('callCredential');
    dispatch(flowActions.setCallCredential(null));
  };

  return (
    <Stack spacing={1} mb={1}>
      <Label
        title='Credential Connection'
        tooltip='This credential connection is used to initiate the inbound dial to start the workflow during testing via the Flow user interface. You can find a list of credential connections in your Mission Control Portal account.'
      />
      <Credential>
        <Stack direction='row' spacing={1} alignItems='center'>
          <DialerSipOutlined sx={{ fontSize: '20px', color: theme.primary }} />
          <p>{username}</p>
        </Stack>
        <Button small customColor='black' onClick={changeCredential}>
          Change
        </Button>
      </Credential>
    </Stack>
  );
};
